import { FieldValues } from "react-hook-form";
import { DEFAULT_SORT_KEY, DEFAULT_SORT_METHOD } from "../utils/browseUrlParams";

type BrowseCacheEligibleArgs<TSearch extends FieldValues> = {
  filters: Partial<TSearch>;
  sortKey: string;
  sortMethod: "ASC" | "DESC";
  cursor?: string;
  allowedFilterKeys?: string[];
};


/**
 * Decide whether the current browse state may send use_cache to the server.
 * @param filters Partial<TSearch> - Active browse filters (URL or form state).
 * @param sortKey string - Current sort column key.
 * @param sortMethod "ASC" | "DESC" - Current sort direction.
 * @param cursor string | undefined - Cursor token when paginating past page 1.
 * @param allowedFilterKeys string[] | undefined - Filter keys permitted in a clean/default browse (e.g. account_type).
 * @returns boolean - True when default sort, no cursor, and only allowed filters are active.
 */
export function isBrowseCacheEligible<TSearch extends FieldValues>({
  filters,
  sortKey,
  sortMethod,
  cursor,
  allowedFilterKeys = [],
}: BrowseCacheEligibleArgs<TSearch>): boolean {

  if (cursor) {
    return false;
  }

  if (sortKey !== DEFAULT_SORT_KEY || sortMethod !== DEFAULT_SORT_METHOD) {
    return false;
  }

  const activeFilterKeys = Object.entries(filters)
    .filter(([, value]) => value !== null && value !== undefined && value !== "")
    .map(([key]) => key);

  return activeFilterKeys.every((key) => allowedFilterKeys.includes(key));
}
